import { useCallback } from 'react';

interface DefinitionTextProps {
  definition: string;
  language?: 'Hebrew' | 'Greek' | string;
  onLinkClick: (strongsNumber: string) => void;
}

/**
 * Parses and renders the definition text with HTML markup (links, bold, italic, underline).
 */
export function DefinitionText({ definition, language, onLinkClick }: DefinitionTextProps) {
  const langPrefix = language?.toLowerCase().startsWith('g') ? 'G' : 'H';

  // Turn bare Strong's numbers (3-5 digits) in plain text into clickable links
  const parseTextForNumbers = useCallback(
    (text: string, startKey: number): { nodes: React.ReactNode[]; nextKey: number } => {
      const nodes: React.ReactNode[] = [];
      let key = startKey;
      // Match standalone 3-5 digit numbers not already inside a tag
      const pattern = /\b(\d{3,5})\b/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          nodes.push(text.substring(lastIndex, match.index));
        }
        const num = match[1];
        const strongsNum = `${langPrefix}${num}`;
        nodes.push(
          <button key={key++} className="strongs-link" onClick={() => onLinkClick(strongsNum)}>
            {num}
          </button>,
        );
        lastIndex = pattern.lastIndex;
      }

      if (lastIndex < text.length) {
        nodes.push(text.substring(lastIndex));
      }

      return { nodes, nextKey: key };
    },
    [langPrefix, onLinkClick],
  );

  // Simple HTML parser for the definition text
  // Handles (case-insensitive): <a href="...">, <b>, <i>, <u>
  const parseDefinition = useCallback(
    (html: string): React.ReactNode[] => {
      const result: React.ReactNode[] = [];
      let remaining = html;
      let keyCounter = 0;

      while (remaining.length > 0) {
        // Find the next tag
        const tagMatch = remaining.match(/<(a|b|i|u)(\s+[^>]*)?>|<\/(a|b|i|u)>/i);

        if (!tagMatch) {
          // No more tags, parse remaining text for bare number references
          const { nodes } = parseTextForNumbers(remaining, keyCounter);
          result.push(...nodes);
          break;
        }

        const tagIndex = tagMatch.index!;

        // Add text before the tag (with number parsing)
        if (tagIndex > 0) {
          const { nodes, nextKey } = parseTextForNumbers(
            remaining.substring(0, tagIndex),
            keyCounter,
          );
          result.push(...nodes);
          keyCounter = nextKey;
        }

        const fullMatch = tagMatch[0];
        const tagName = (tagMatch[1] || tagMatch[3]).toLowerCase();

        if (fullMatch.startsWith('</')) {
          // Closing tag - skip it (handled by opening tag logic)
          remaining = remaining.substring(tagIndex + fullMatch.length);
          continue;
        }

        // Opening tag - find the closing tag
        const closingTag = `</${tagName}>`;
        const closingIndex = remaining
          .toLowerCase()
          .indexOf(closingTag, tagIndex + fullMatch.length);

        if (closingIndex === -1) {
          // No closing tag found, treat as text
          result.push(fullMatch);
          remaining = remaining.substring(tagIndex + fullMatch.length);
          continue;
        }

        const innerContent = remaining.substring(tagIndex + fullMatch.length, closingIndex);
        const key = keyCounter++;

        if (tagName === 'a') {
          // Extract href from the tag
          const hrefMatch = fullMatch.match(/href=["']([^"']+)["']/i);
          if (hrefMatch) {
            const href = hrefMatch[1];
            // Parse strongs number from href like "/strongs/hebrew/7218"
            const parts = href.toUpperCase().split('/');
            if (parts.length >= 4) {
              const lang = parts[2].substring(0, 1); // H or G
              const num = parts[3];
              const strongsNum = `${lang}${num}`;

              result.push(
                <button key={key} className="strongs-link" onClick={() => onLinkClick(strongsNum)}>
                  {innerContent}
                </button>,
              );
            } else {
              result.push(
                <span key={key} className="underline">
                  {innerContent}
                </span>,
              );
            }
          } else {
            result.push(<span key={key}>{innerContent}</span>);
          }
        } else if (tagName === 'b') {
          result.push(
            <strong key={key} className="font-bold">
              {parseDefinition(innerContent)}
            </strong>,
          );
        } else if (tagName === 'i') {
          result.push(
            <em key={key} className="italic">
              {parseDefinition(innerContent)}
            </em>,
          );
        } else if (tagName === 'u') {
          // Greek entries underline the cited root word: "a primary verb <U>δίω</U>"
          result.push(<u key={key}>{parseDefinition(innerContent)}</u>);
        }

        remaining = remaining.substring(closingIndex + closingTag.length);
      }

      return result;
    },
    [onLinkClick, parseTextForNumbers],
  );

  return <>{parseDefinition(definition)}</>;
}
